import { NodeType } from '@rrweb/types'
import { countOperationKey } from '../../lib/operation-timing.ts'
import { domRevision, type DomRecord, type DomRevision } from '../../recording/dom-changes.ts'
import type { OfflineDom, OfflineEl } from '../layout-shift/rrweb.ts'
import type { SightmapComponent } from './schema.ts'

// Attributes the user or the app rewrites in place: the element's current state, not which instance it is.
const STATE_ATTRIBUTES = new Set(['checked', 'selected', 'disabled', 'open', 'hidden', 'data-state', 'aria-checked', 'aria-selected',
  'aria-pressed', 'aria-expanded', 'aria-disabled', 'aria-invalid', 'aria-current', 'aria-valuenow', 'aria-hidden'])

/** A static prior: the attribute is state on every element. */
export function stateAttribute(name: string): boolean {
  return STATE_ATTRIBUTES.has(name)
}

/** Generated ids: React useId, dnd-kit live regions, long digit runs, hex hashes and hex suffixes (UUIDs included). */
const GENERATED_ID = /(?:^|[-:])_?r[_:]|^Dnd(?:LiveRegion|DescribedBy)-\d+$|\d{4,}|^[0-9a-f]{8,}$|-[0-9a-f]{6,}$/i
/** CMS/bundler tokens: long, no separators, mixed case and digits. */
const GENERATED_TOKEN = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9A-Za-z]{18,}$/

/** Generated-ness only: GENERATED_ID and long mixed tokens. Syntax checks stay where a value is emitted. */
export function generatedValue(value: string): boolean {
  return GENERATED_ID.test(value) || GENERATED_TOKEN.test(value)
}

export interface IdentityKind { tag: string; type?: string; role?: string }
export interface IdentityFeatures extends IdentityKind {
  /** Attribute values that are no static prior and equal in every observed state. */
  values: Readonly<Record<string, string>>
  /** Own text, by the same rule. */
  label?: string
  /** Element children present in every observed state, in order. */
  children: readonly IdentityKind[]
}
/** The observed (published) states of one L0 scope up to `dom`, oldest first. */
export interface FeatureHistory { readonly dom: OfflineDom; readonly revisions: readonly DomRevision[] }

/** What differs between one published state and the previous one, per element. */
interface StateVariation { attributes: Map<number, Set<string>>; text: Set<number>; transientChildren: Set<number> }
const variations = new WeakMap<DomRevision, StateVariation>()
const histories = new WeakMap<OfflineDom, FeatureHistory>()

function attribute(record: DomRecord, name: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(record.attrs, name) ? record.attrs[name] : undefined
}
function ownText(records: DomRevision['records'], record: DomRecord): string {
  return record.children.map(id => records.get(id)).filter(child => child?.type === NodeType.Text).map(child => child!.text).join('').replace(/\s+/g, ' ').trim()
}
function elementChildren(records: DomRevision['records'], record: DomRecord): number[] {
  return record.children.filter(id => records.get(id)?.type === NodeType.Element)
}
// The values a recycled element would carry to its new node; static priors are state anyway.
function recycleVector(records: DomRevision['records'], record: DomRecord): string {
  return JSON.stringify([record.tag, Object.entries(record.attrs).filter(([name]) => !stateAttribute(name)).sort(), ownText(records, record)])
}

/** Recycling candidates (a node taking over another node's values) are diagnostics only: one revision
 *  cannot tell recycling from state, so every change within the scope stays state. */
function countRecycleCandidates(previous: DomRevision, revision: DomRevision, changed: readonly number[]): void {
  const vectors = new Map<DomRevision, Map<number, string | null>>([[previous, new Map()], [revision, new Map()]])
  const vector = (state: DomRevision, id: number): string | null => {
    const memo = vectors.get(state)!
    if (!memo.has(id)) {
      const record = state.records.get(id)
      memo.set(id, record?.type === NodeType.Element ? recycleVector(state.records, record) : null)
    }
    return memo.get(id)!
  }
  const changes = (id: number) => { const before = vector(previous, id); return before !== null && before !== vector(revision, id) }
  for (const id of changed) {
    if (!changes(id)) continue
    const record = revision.records.get(id)!
    const parent = record.parent === null ? undefined : revision.records.get(record.parent)
    const siblings = (parent ? elementChildren(revision.records, parent) : []).filter(sibling => sibling !== id && revision.records.get(sibling)?.tag === record.tag)
    if (!siblings.length) continue
    const old = [id, ...siblings].map(sibling => vector(previous, sibling)).filter((value): value is string => value !== null)
    if (new Set(old).size === old.length && siblings.some(sibling => changes(sibling) && vector(revision, sibling) === vector(previous, id))) {
      countOperationKey('sightmap.identity.recycle.permuted', revision.document, String(id))
    }
    if (siblings.every(changes)) countOperationKey('sightmap.identity.recycle.replaced', revision.document, String(id))
  }
}

function stateVariation(previous: DomRevision, revision: DomRevision): StateVariation {
  const cached = variations.get(revision)
  if (cached) return cached
  const variation: StateVariation = { attributes: new Map(), text: new Set(), transientChildren: new Set() }
  const elements = new Set<number>()
  for (const id of revision.changes.keys()) {
    elements.add(id)
    for (const records of [previous.records, revision.records]) {
      const record = records.get(id)
      if (record?.type === NodeType.Text && record.parent !== null) elements.add(record.parent)
    }
  }
  const changed: number[] = []
  for (const id of elements) {
    const before = previous.records.get(id), after = revision.records.get(id)
    if (before?.type !== NodeType.Element || after?.type !== NodeType.Element) continue
    changed.push(id)
    const names = [...new Set([...Object.keys(before.attrs), ...Object.keys(after.attrs)])]
      .filter(name => attribute(before, name) !== attribute(after, name))
    if (names.length) variation.attributes.set(id, new Set(names))
    if (ownText(previous.records, before) !== ownText(revision.records, after)) variation.text.add(id)
    const kept = new Set(elementChildren(previous.records, before))
    for (const child of elementChildren(revision.records, after)) if (!kept.delete(child)) variation.transientChildren.add(child)
    for (const child of kept) variation.transientChildren.add(child)
  }
  countRecycleCandidates(previous, revision, changed)
  variations.set(revision, variation)
  return variation
}

/** The only constructor: folds `dom`'s own revision chain, from its oldest published state. */
export function featureHistory(dom: OfflineDom): FeatureHistory {
  const cached = histories.get(dom)
  if (cached) return cached
  const revisions: DomRevision[] = []
  for (let cursor = domRevision(dom); cursor; cursor = cursor.previous) revisions.push(cursor)
  revisions.reverse()
  for (let index = 1; index < revisions.length; index++) stateVariation(revisions[index - 1]!, revisions[index]!)
  const history = { dom, revisions }
  histories.set(dom, history)
  return history
}

function variationsOf(history: FeatureHistory): StateVariation[] {
  return history.revisions.slice(1).map((revision, index) => stateVariation(history.revisions[index]!, revision))
}

/** The features of `node` that are no state in its L0 scope. A DOM without history is one state: only
 *  the static priors are state. */
export function identityFeatures(history: FeatureHistory, node: OfflineEl): IdentityFeatures {
  if (history.dom.getById(node.id) !== node) throw new Error(`Node ${node.id} is not an element of the feature history's DOM`)
  const states = variationsOf(history)
  const identityValues = (element: OfflineEl) => {
    const varied = new Set(states.flatMap(state => [...state.attributes.get(element.id) ?? []]))
    return Object.entries(element.attrs).filter(([name]) => !stateAttribute(name) && !varied.has(name))
  }
  const kind = (element: OfflineEl): IdentityKind => {
    const values = new Map(identityValues(element))
    const type = values.get('type'), role = values.get('role')
    return { tag: element.tag, ...(type !== undefined ? { type } : {}), ...(role !== undefined ? { role } : {}) }
  }
  const label = states.some(state => state.text.has(node.id)) ? '' : node.ownText
  return {
    ...kind(node),
    values: Object.fromEntries(identityValues(node)),
    ...(label ? { label } : {}),
    children: node.children.filter(child => !states.some(state => state.transientChildren.has(child.id))).map(kind),
  }
}
// Input types whose value attribute is authored markup, not entered content.
const STATIC_VALUE_INPUTS = new Set(['button', 'checkbox', 'hidden', 'image', 'radio', 'reset', 'submit'])
const TEXT_EDIT_ACTIONS = new Set(['fill', 'type'])

function entersValue(component: SightmapComponent): boolean {
  const structure = component.structure
  if (!structure) return !!component.actions?.some(action => TEXT_EDIT_ACTIONS.has(action) || action === 'select')
  return structure.tag === 'textarea' || structure.tag === 'select'
    || structure.tag === 'input' && !STATIC_VALUE_INPUTS.has(structure.type ?? 'text')
}

// An element whose own text content the user edits: a textarea, a textbox or contenteditable region.
function editableText(component: SightmapComponent): boolean {
  const selectors = Array.isArray(component.selector) ? component.selector : [component.selector]
  return component.structure?.tag === 'textarea' || component.structure?.role === 'textbox'
    || selectors.some(selector => selector.includes('contenteditable'))
}

// A control's text is edited content when the map fills it, unless it is an input or select: their
// text is the accessible name, never the entered value.
function editsText(component: SightmapComponent): boolean {
  const tag = component.structure?.tag
  return editableText(component)
    || !!component.actions?.some(action => TEXT_EDIT_ACTIONS.has(action)) && tag !== 'input' && tag !== 'select'
}

function containsEditableText(component: SightmapComponent): boolean {
  return (component.children ?? []).some(child => editableText(child) || containsEditableText(child))
}

function descendant(component: SightmapComponent, name: string): SightmapComponent | undefined {
  for (const child of component.children ?? []) {
    if (child.name === name) return child
    const nested = descendant(child, name)
    if (nested) return nested
  }
}

// Classifies every form of the property grammar (resolveSightmapProperties), following PATH.prop to the
// descendant property it reads.
function readsState(component: SightmapComponent, extract: string, seen = new Set<string>()): boolean {
  if (extract.startsWith('state:')) return true
  if (extract.startsWith('exists:')) return false
  if (extract === 'raw_text') return editsText(component)
  if (extract === 'text') return editsText(component) || containsEditableText(component)
  if (extract.startsWith('attr=')) {
    const attribute = extract.slice(5)
    return stateAttribute(attribute) || attribute === 'value' && entersValue(component)
  }
  const parts = extract.split('.')
  if (parts.length < 2) return false
  const name = parts.pop()!
  let node: SightmapComponent | undefined = component
  for (const part of parts) node = node && descendant(node, part)
  if (!node) return false
  const declared = (node.properties ?? []).filter(property => property.name === name)
  if (!declared.length) return (name === 'text' || name === 'raw_text') && readsState(node, name, seen)
  const key = `${node.id ?? node.name}.${name}`
  if (seen.has(key)) return false
  seen.add(key)
  return declared.some(property => readsState(node, property.extract, seen))
}

/** The static reasons a definition states for a value never identifying an instance. */
export type NonIdentityReason = 'aggregate container text' | 'mutable state'

/** Why a declared property can never identify an instance, or null when it can. */
export function nonIdentityReason(component: SightmapComponent, name: string): NonIdentityReason | null {
  const extracts = (component.properties ?? []).filter(property => property.name === name).map(property => property.extract)
  if (component.params?.includes(name) && component.children?.length && extracts.includes('text')) return 'aggregate container text'
  if (extracts.some(extract => readsState(component, extract))) return 'mutable state'
  return null
}

/** Identity parameters the definition declares, minus container-wide text and mutable state. */
export function identityParameterNames(component: SightmapComponent): string[] {
  return (component.params ?? []).filter(name => !nonIdentityReason(component, name))
}
